import { useKeycloakToken } from '../common/useKeycloak';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SourcesList, { sourceSortValues } from './SourcesList';
import { DestinationType, ProviderType, Organization, SourceWithRoutes } from '../common/ObjectTypes';
import { loadOrganizations } from "../../api/organizations/loadOrganizations";
import { filter_page_size, page_size } from '../../api/apiUtils';
import loadSources from '../../api/sources/loadSources';
import { loadProviderTypes } from '../../api/routes/loadProviderTypesFilter';
import { getRoutesByIntegration } from '../../api/routes/routesApi';
import loadDestinationUrls from '../../api/destinations/loadDestinationUrls';
import { loadDestinationTypes } from '../../api/destinations/loadDestinationTypes';
import { useKeycloak } from '@react-keycloak/web';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

const SourcesPage = () => {
    const { t } = useTranslation();
    type FilterTypes = {
        provider_type__in: FilterTags[],
        destination_url__in: FilterTags[],
        destination_type__in: FilterTags[],
        owner__in: FilterTags[],
        ordering: string,
        search: string,
        offset: number
    };
    type FilterTags = {
        name: string,
        id: string
    };
    const defaultSourceFilter: FilterTypes = {
        provider_type__in: [],
        destination_url__in: [],
        destination_type__in: [],
        owner__in: [],
        ordering: "-external_id",
        search: "",
        offset: 0
    };
    const navigate = useNavigate();
    const [token, showLoginModal, setShowLoginModal] = useKeycloakToken();
    const { keycloak } = useKeycloak();
    const [sources, setSources] = useState<SourceWithRoutes[]>([]);
    const [totalSources, setTotalSources] = useState<number>(0);
    const [organizations, setOrganizations] = useState<Organization[]>([]);
    const [loadingOrganizations, setLoadingOrganizations] = useState(false);
    const [destinationTypes, setDestinationTypes] = useState<DestinationType[]>([]);
    const [loadingDestinationTypes, setLoadingDestinationTypes] = useState(false);
    const [providerTypes, setProviderTypes] = useState<ProviderType[]>([]);
    const [loadingProviderTypes, setLoadingProviderTypes] = useState(false);
    const [destinationUrls, setDestinationUrls] = useState<any[]>([]);
    const [loadingDestinationUrls, setLoadingDestinationUrls] = useState(false);
    const [reloadSources, setReloadSources] = useState(true);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [sourceFilter, setSourceFilter] = useState<FilterTypes>(() => {
        const savedFilter = sessionStorage.getItem('sourceFilter');
        const filter = savedFilter ? { ...defaultSourceFilter, ...JSON.parse(savedFilter) } : defaultSourceFilter;
        // Persisted ordering may predate the Sort By dropdown (old column-header
        // sorts saved e.g. integration__id) — reset it so the dropdown's
        // displayed selection always matches the API ordering.
        if (!sourceSortValues.includes(filter.ordering)) filter.ordering = defaultSourceFilter.ordering;
        return filter;
    });

    const loadSourcesData = async (filter: FilterTypes) => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            const [source_results, total_sources, error] = await loadSources(token, "", filter);
            if (!error) {
                const sources_with_routes: SourceWithRoutes[] = source_results.map(source => ({
                    source,
                    routes: []
                }));
                setSources(sources_with_routes);
                setTotalSources(total_sources);
                setPageLoaded(true);
            }
            // Always settle the flag — a failed request must not leave the
            // refresh button disabled and spinning forever.
            setReloadSources(false);
        }
    }

    const loadProviderTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingProviderTypes(true);
            try {
                let accumulated: ProviderType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadProviderTypes(token, typesOffset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + filter_page_size >= total) break;
                    typesOffset += filter_page_size;
                }
                setProviderTypes(accumulated);
            } finally {
                setLoadingProviderTypes(false);
            }
        }
    }

    const loadDestinationURlsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationUrls(true);
            try {
                let accumulated: string[] = [];
                let urlsOffset = 0;
                while (true) {
                    const [dest_urls, total, error] = await loadDestinationUrls(token, urlsOffset, "", accumulated);
                    if (error) break;
                    accumulated = dest_urls;
                    if (urlsOffset + filter_page_size >= total) break;
                    urlsOffset += filter_page_size;
                }
                setDestinationUrls([...new Set(accumulated)]);
            } finally {
                setLoadingDestinationUrls(false);
            }
        }
    }

    const loadDestinationTypesData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingDestinationTypes(true);
            try {
                let accumulated: DestinationType[] = [];
                let typesOffset = 0;
                while (true) {
                    const [types, total, error] = await loadDestinationTypes(token, typesOffset, "", accumulated);
                    if (error) break;
                    accumulated = types;
                    if (typesOffset + filter_page_size >= total) break;
                    typesOffset += filter_page_size;
                }
                setDestinationTypes(accumulated);
            } finally {
                setLoadingDestinationTypes(false);
            }
        }
    }

    const loadOrganizationsData = async () => {
        if (token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            setLoadingOrganizations(true);
            try {
                let accumulated: Organization[] = [];
                let orgsOffset = 0;
                while (true) {
                    const [orgs, total, error] = await loadOrganizations(token, orgsOffset, "", accumulated);
                    if (error) break;
                    accumulated = orgs;
                    if (orgsOffset + filter_page_size >= total) break;
                    orgsOffset += filter_page_size;
                }
                setOrganizations(accumulated);
            } finally {
                setLoadingOrganizations(false);
            }
        }
    }

    useEffect(() => {
        sessionStorage.setItem('sourceFilter', JSON.stringify(sourceFilter));
        if (reloadSources) loadSourcesData(sourceFilter);
    }, [token, reloadSources, sourceFilter]);

    useEffect(() => {
        if (token) {
            loadDestinationURlsData();
            loadProviderTypesData();
            loadDestinationTypesData();
            loadOrganizationsData();
        }
    }, [token]);

    const handlePageClick = (e: any) => {
        setSourceFilter(prev => ({ ...prev, offset: e.selected * page_size }));
        setReloadSources(true);
    }

    const handleGlobalSearch = (searchSubStr: string) => {
        setSourceFilter(prev => ({ ...prev, search: searchSubStr, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterOwner = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, owner__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterEndpoint = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, destination_url__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterProviderType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, provider_type__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleFilterDestinationType = (selected: any) => {
        const tags: FilterTags[] = selected ? [{ name: selected.label, id: selected.value }] : [];
        setSourceFilter(prev => ({ ...prev, destination_type__in: tags, offset: 0 }));
        setReloadSources(true);
    }

    const handleSortBy = (selected: { label: string, value: string }) => {
        setSourceFilter(prev => ({ ...prev, ordering: selected.value, offset: 0 }));
        setReloadSources(true);
    }

    const handleToggleDestinationDetailsModal = async (dest_id: string) => {
        if (dest_id && token && typeof token === 'string' && !keycloak.isTokenExpired()) {
            navigate(`/sources/destination/${dest_id}/configuration`, { replace: false });
        }
    }

    // Sources rows carry the provider integration's id; route pages are addressed
    // by the route record, so resolve it first. Cached per integration: the same
    // row can be clicked repeatedly, and a second click while the first lookup is
    // in flight must not fire another query.
    const routeRecordIds = useRef<Record<string, string>>({});
    const resolvingRoute = useRef<Set<string>>(new Set());
    const handleViewRouteDetails = async (integration_id: string) => {
        if (!(keycloak.token && !keycloak.isTokenExpired())) return;
        const cached = routeRecordIds.current[integration_id];
        if (cached) return navigate(`/routes/${cached}`, { replace: false });
        if (resolvingRoute.current.has(integration_id)) return;
        resolvingRoute.current.add(integration_id);
        try {
            // Explicit ordering: results[0] out of an unordered list meant a
            // backend ordering change could silently switch which Route a row opens.
            const data = await getRoutesByIntegration(keycloak.token, "provider", integration_id, { ordering: "id" });
            const recordId = data.results?.[0]?.id;
            if (recordId) {
                routeRecordIds.current[integration_id] = recordId;
                navigate(`/routes/${recordId}`, { replace: false });
                return;
            }
            // Silently doing nothing reads as a broken link, so say which it is.
            toast.error(data.ok
                ? t("This source's provider has no Route yet.")
                : t("Something went wrong. Please try again or contact support."),
                { position: toast.POSITION.TOP_RIGHT });
        } finally {
            resolvingRoute.current.delete(integration_id);
        }
    }

    const toSingleValue = (tags: FilterTags[]) =>
        tags.length > 0 ? { label: tags[0].name, value: tags[0].id } : null;

    const endpointSelectValue = toSingleValue(sourceFilter.destination_url__in);
    const ownerSelectValue = toSingleValue(sourceFilter.owner__in);
    const providerTypeSelectValue = toSingleValue(sourceFilter.provider_type__in);
    const destinationTypeSelectValue = toSingleValue(sourceFilter.destination_type__in);

    return (
            <div>
                <SourcesList
                    sources={sources}
                    totalCount={totalSources}
                    take={page_size}
                    offset={sourceFilter.offset}
                    onPageClicked={handlePageClick}
                    loading={reloadSources}
                    organizations={organizations}
                    loadingOrganizations={loadingOrganizations}
                    destinationTypes={destinationTypes}
                    loadingDestinationTypes={loadingDestinationTypes}
                    destinationUrls={destinationUrls}
                    loadingDestinationUrls={loadingDestinationUrls}
                    providerTypes={providerTypes}
                    loadingProviderTypes={loadingProviderTypes}
                    globalSearchStr={sourceFilter.search}
                    onChangeGlobalSearch={handleGlobalSearch}
                    onRefresh={() => setReloadSources(true)}
                    onChangeOwner={handleFilterOwner}
                    ownerFilterValue={ownerSelectValue}
                    onChangeEndpoint={handleFilterEndpoint}
                    endpointFilterValue={endpointSelectValue}
                    onChangeProviderType={handleFilterProviderType}
                    providerTypeFilterValue={providerTypeSelectValue}
                    onChangeDestinationType={handleFilterDestinationType}
                    destinationTypeFilterValue={destinationTypeSelectValue}
                    ordering={sourceFilter.ordering}
                    onChangeSortBy={handleSortBy}
                    onViewDetails={handleToggleDestinationDetailsModal}
                    onViewRouteDetails={handleViewRouteDetails}
                />
            </div>
        );
};

export default SourcesPage;
